import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Flame, Target, Share2, Plus, Timer, ArrowLeftRight,
  Activity, Calendar, Users, ChevronRight, X,
  Sun, Moon, LayoutDashboard, Radio, Copy, RefreshCw, Sparkles, Rocket,
} from 'lucide-react';
import {
  getCachedName, setCachedName, getShadowId,
  saveMatch, updateElo, computeWinStreak, getUmpire, incrementUmpire,
} from '../lib/localStore';
import {
  PRESETS, buildClassicPreset, newMatchState, applyPoint, undoPoint,
} from '../lib/scoringEngine';
import { telemetry, fetchPublicAdConfig } from '../lib/telemetry';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { ModeToggle } from '../components/ModeToggle';
import LoopingAdBanner from '../components/LoopingAdBanner';
import BottomNav from '../components/BottomNav';
import { requestLocation, hasAskedLocation, getCachedGeo } from '../lib/geolocation';
import { startLiveWatch, pushLiveWatch, watchUrl } from '../lib/liveWatch';
import { launchFeedback } from '../lib/feedback';
// QuickPlay sub-components extracted for maintainability:
import ElapsedTimer from '../components/quickplay/ElapsedTimer';
import IntervalAdCard from '../components/quickplay/IntervalAdCard';
import BragCard from '../components/quickplay/BragCard';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3ec18717-9700-48f1-a5a2-71ac0edd3a6f/artifacts/29waugc4_WhatsApp%20Image%202026-03-06%20at%2016.26.44.jpeg';

// ----------------------- Elapsed Timer -----------------------
// Extracted to /components/quickplay/ElapsedTimer.js
// ----------------------- Setup Form -----------------------
function SetupForm({ onStart }) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated } = useAuth();
  const [presetId, setPresetId] = useState('CLASSIC_21');
  const [matchType, setMatchType] = useState('singles');
  const [sideA, setSideA] = useState(getCachedName());
  const [sideB, setSideB] = useState('');
  const [partnerA, setPartnerA] = useState('');
  const [partnerB, setPartnerB] = useState('');
  const [pointsPerGame, setPointsPerGame] = useState(21);
  const [bestOf, setBestOf] = useState(1);

  // Persist the user's name as they type so it survives refresh.
  useEffect(() => {
    if (sideA.trim()) setCachedName(sideA);
  }, [sideA]);

  useEffect(() => {
    telemetry.registerShadow();
    // One-time location permission prompt (browser will only show it once).
    if (!hasAskedLocation()) {
      requestLocation({ silent: true });
    }
  }, []);

  const handleStart = (e) => {
    e.preventDefault();
    if (!sideA.trim() || !sideB.trim()) {
      toast.error('Enter both player names');
      return;
    }
    if (matchType === 'doubles' && (!partnerA.trim() || !partnerB.trim())) {
      toast.error('Enter partner names for doubles');
      return;
    }
    setCachedName(sideA);
    // Tiny "launch" feedback — 80ms haptic + brief upward audio sweep.
    launchFeedback();
    // Build preset: BWF is fixed; Classic honours user's points/best-of choices.
    const preset = presetId === 'BWF_3x15'
      ? PRESETS.BWF_3x15
      : buildClassicPreset({ pointsPerGame, bestOf });
    onStart(newMatchState({ preset, matchType, sideA, sideB, partnerA, partnerB }));
  };

  const streakA = computeWinStreak(sideA);

  return (
    <div className="min-h-screen bg-background flex flex-col safe-top safe-bottom" data-testid="quickplay-setup">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-6 pb-2">
        <button
          onClick={() => navigate('/streaks')}
          className="flex items-center gap-2 rounded-full bg-card/80 border border-border px-3 py-1.5 text-xs"
          data-testid="quickplay-streaks-link"
        >
          <Timer className="h-3.5 w-3.5 text-primary" /> My Streaks
        </button>
        <img src={LOGO_URL} alt="LoveAll" className="h-12 w-12 rounded-full object-cover" />
        <div className="flex items-center gap-1.5">
          <button
            onClick={toggleTheme}
            className="h-8 w-8 rounded-full bg-card/80 border border-border flex items-center justify-center"
            aria-label="Toggle theme"
            data-testid="quickplay-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={() => navigate('/rivals')}
            className="flex items-center gap-2 rounded-full bg-card/80 border border-border px-3 py-1.5 text-xs"
            data-testid="quickplay-rivals-link"
          >
            <Users className="h-3.5 w-3.5 text-secondary" /> Rivals
          </button>
        </div>
      </div>

      {/* Tournament secondary CTA */}
      <div className="flex justify-center px-5 pt-3">
        <ModeToggle />
      </div>

      {/* Slogan */}
      <div className="text-center px-5 pt-4 pb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold uppercase tracking-tight inline-flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
          <span>Score. Track. <span className="text-primary">Dominate.</span></span>
          {/* Inline shuttlecock — cork at the bottom (resting orientation),
              feathers fan outward and upward. Animates with a gentle bobbing. */}
          <svg
            viewBox="0 0 64 64"
            className="h-7 w-7 sm:h-9 sm:w-9 shrink-0 animate-bobbing"
            aria-hidden="true"
            data-testid="slogan-shuttle"
          >
            <defs>
              <linearGradient id="cork" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(36 100% 65%)" />
                <stop offset="100%" stopColor="hsl(28 80% 38%)" />
              </linearGradient>
            </defs>
            {/* Feathers — fan upward and outward from the cork */}
            <g stroke="hsl(190 100% 75%)" strokeWidth="1.6" strokeLinecap="round" fill="rgba(255,255,255,0.85)">
              <path d="M32 42 L10 10" />
              <path d="M32 42 L20 6" />
              <path d="M32 42 L32 4" />
              <path d="M32 42 L44 6" />
              <path d="M32 42 L54 10" />
              {/* Upper hoop connecting feather tips */}
              <ellipse cx="32" cy="9" rx="22" ry="4" fill="rgba(255,255,255,0.6)" />
            </g>
            {/* White tape band just above the cork */}
            <ellipse cx="32" cy="42" rx="9" ry="3" fill="#ffffff" stroke="hsl(36 30% 60%)" strokeWidth="0.6" />
            {/* Cork dome at the bottom */}
            <path d="M23 42 A9 10 0 0 0 41 42 Z" fill="url(#cork)" stroke="hsl(28 60% 30%)" strokeWidth="0.8" />
            {/* Highlight on cork */}
            <ellipse cx="29" cy="47" rx="2.5" ry="1.2" fill="rgba(255,255,255,0.55)" />
          </svg>
        </h1>
      </div>

      {/* Form Card */}
      <div className="flex-1 px-5">
        <form onSubmit={handleStart} className="max-w-md mx-auto space-y-5">
          {/* Preset */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 block">Format</Label>
            <ToggleGroup
              type="single"
              value={presetId}
              onValueChange={(v) => v && setPresetId(v)}
              className="grid grid-cols-2 gap-2"
            >
              <ToggleGroupItem
                value="BWF_3x15"
                className="h-14 rounded-xl border-2 data-[state=on]:border-primary data-[state=on]:bg-primary/15 data-[state=on]:text-foreground flex-col gap-0.5"
                data-testid="preset-bwf"
              >
                <span className="font-heading font-bold">BWF 3x15 Sprint</span>
                <span className="text-[10px] text-muted-foreground">First to 15, cap 21</span>
              </ToggleGroupItem>
              <ToggleGroupItem
                value="CLASSIC_21"
                className="h-14 rounded-xl border-2 data-[state=on]:border-primary data-[state=on]:bg-primary/15 data-[state=on]:text-foreground flex-col gap-0.5"
                data-testid="preset-classic"
              >
                <span className="font-heading font-bold">Classic 21</span>
                <span className="text-[10px] text-muted-foreground">Single set, cap 30</span>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Match Type */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 block">Match Type</Label>
            <ToggleGroup
              type="single"
              value={matchType}
              onValueChange={(v) => v && setMatchType(v)}
              className="grid grid-cols-2 gap-2"
            >
              <ToggleGroupItem value="singles" className="h-11 rounded-xl border-2 data-[state=on]:border-primary data-[state=on]:bg-primary/15 data-[state=on]:text-foreground" data-testid="match-type-singles">
                Singles
              </ToggleGroupItem>
              <ToggleGroupItem value="doubles" className="h-11 rounded-xl border-2 data-[state=on]:border-primary data-[state=on]:bg-primary/15 data-[state=on]:text-foreground" data-testid="match-type-doubles">
                Doubles
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Classic-only: Points/Game + Best Of */}
          {presetId === 'CLASSIC_21' && (
            <div className="grid grid-cols-2 gap-3" data-testid="classic-options">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 block">Points / Game</Label>
                <ToggleGroup
                  type="single"
                  value={String(pointsPerGame)}
                  onValueChange={(v) => v && setPointsPerGame(Number(v))}
                  className="grid grid-cols-3 gap-1.5"
                >
                  {[11, 15, 21].map(p => (
                    <ToggleGroupItem
                      key={`pts-${p}`}
                      value={String(p)}
                      className="h-10 rounded-lg border-2 text-sm font-bold data-[state=on]:border-primary data-[state=on]:bg-primary/15 data-[state=on]:text-foreground"
                      data-testid={`points-${p}`}
                    >
                      {p}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 block">Best Of</Label>
                <ToggleGroup
                  type="single"
                  value={String(bestOf)}
                  onValueChange={(v) => v && setBestOf(Number(v))}
                  className="grid grid-cols-3 gap-1.5"
                >
                  {[1, 3, 5].map(n => (
                    <ToggleGroupItem
                      key={`bo-${n}`}
                      value={String(n)}
                      className="h-10 rounded-lg border-2 text-sm font-bold data-[state=on]:border-primary data-[state=on]:bg-primary/15 data-[state=on]:text-foreground"
                      data-testid={`best-of-${n}`}
                    >
                      {n}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            </div>
          )}

          {/* Players */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-3 space-y-2">
              <p className="text-[10px] font-bold text-primary uppercase">Side A {matchType === 'doubles' ? '(You & Partner)' : ''}</p>
              <Input
                value={sideA}
                onChange={(e) => setSideA(e.target.value)}
                placeholder="Player A name"
                className="h-10"
                required
                data-testid="side-a-name"
              />
              {streakA > 0 && (
                <p className="inline-flex items-center text-[10px] font-bold text-orange-500" data-testid="quickplay-your-streak">
                  <Flame className="h-3 w-3 fill-orange-500 mr-0.5" /> Win streak: {streakA}
                </p>
              )}
              {matchType === 'doubles' && (
                <Input value={partnerA} onChange={(e) => setPartnerA(e.target.value)} placeholder="Partner" className="h-10" data-testid="side-a-partner" />
              )}
            </div>
            <div className="rounded-xl border-2 border-secondary/30 bg-secondary/5 p-3 space-y-2">
              <p className="text-[10px] font-bold text-secondary uppercase">Side B (Opponent)</p>
              <Input value={sideB} onChange={(e) => setSideB(e.target.value)} placeholder="Opponent name" className="h-10" required data-testid="side-b-name" />
              {matchType === 'doubles' && (
                <Input value={partnerB} onChange={(e) => setPartnerB(e.target.value)} placeholder="Partner" className="h-10" data-testid="side-b-partner" />
              )}
            </div>
          </div>

          {/* BIG CTA */}
          <Button
            type="submit"
            size="lg"
            className="w-full h-20 rounded-2xl text-2xl font-heading font-extrabold uppercase tracking-wide bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-xl active:scale-[0.98] transition-transform"
            data-testid="quickplay-start-btn"
          >
            <Rocket className="mr-3 h-7 w-7" />
            Start Match
          </Button>

          {/* Looping Ad Banner */}
          <div className="pt-2">
            <LoopingAdBanner variant="inline" />
          </div>
        </form>
      </div>

      {/* Tiny owner login link */}
      <div className="text-center pb-6 pt-4">
        {isAuthenticated ? (
          <button
            onClick={() => navigate('/dashboard')}
            className="text-[11px] text-muted-foreground/70 hover:text-foreground underline underline-offset-2 inline-flex items-center gap-1"
            data-testid="owner-dashboard-link"
          >
            <LayoutDashboard className="h-3 w-3" /> Go to Owner Dashboard
          </button>
        ) : (
          <button
            onClick={() => navigate('/owner-login')}
            className="text-[11px] text-muted-foreground/50 hover:text-muted-foreground underline underline-offset-2"
            data-testid="owner-login-link"
          >
            Own a Club, Venue or School? Sign in →
          </button>
        )}
      </div>
    </div>
  );
}



// ----------------------- Scoring Board -----------------------
function ScoringBoard({ initialState, onExit }) {
  const [state, setState] = useState(initialState);
  const [showInterval, setShowInterval] = useState(false);
  const [adConfig, setAdConfig] = useState(null);
  const [umpireToast, setUmpireToast] = useState(null);
  // Stack of sides scored ('A' or 'B') — used by smart Undo to pop the last point.
  const [pointHistory, setPointHistory] = useState([]);
  // Live-watch (spectator URL) state
  const [liveSession, setLiveSession] = useState(null); // { shortId, hostToken }
  const [showLiveCard, setShowLiveCard] = useState(false);
  const [goingLive, setGoingLive] = useState(false);

  useEffect(() => {
    fetchPublicAdConfig().then(setAdConfig);
  }, []);

  // Auto-push state to spectator endpoint whenever it changes (only if live).
  useEffect(() => {
    if (!liveSession) return;
    pushLiveWatch(liveSession.shortId, liveSession.hostToken, {
      games: state.games,
      currentGameIdx: state.currentGameIdx,
      winner: state.winner,
    });
  }, [liveSession, state.games, state.currentGameIdx, state.winner]);

  const haptic = (ms = 25) => {
    // Vibration API is best-effort; silently ignore on unsupported devices.
    try { navigator.vibrate?.(ms); } catch { /* unsupported */ }
  };

  const handleScore = (side) => {
    haptic(20);
    setPointHistory(h => [...h, side]);
    setState(prev => {
      const { state: next, flags } = applyPoint(prev, side);
      if (flags.intervalTriggered) {
        setShowInterval(true);
      }
      if (flags.sideSwapTriggered) {
        toast.info('⚡ Side change! Swap ends.', { duration: 4000 });
        haptic([30, 60, 30]);
      }
      if (flags.gameWinner && !flags.matchWinner) {
        toast.success(`Game ${prev.currentGameIdx + 1} → ${flags.gameWinner === 'A' ? next.sideA : next.sideB}!`);
        haptic([50, 50, 50]);
      }
      if (flags.matchWinner) {
        const scoreA = next.games.reduce((s, g) => s + g.scoreA, 0);
        const scoreB = next.games.reduce((s, g) => s + g.scoreB, 0);
        saveMatch({
          id: next.id,
          presetId: next.presetId,
          matchType: next.matchType,
          sideA: next.sideA,
          sideB: next.sideB,
          partnerA: next.partnerA,
          partnerB: next.partnerB,
          scoreA,
          scoreB,
          games: next.games,
          winner: next.winner,
          startedAt: next.startedAt,
          completedAt: next.completedAt,
          durationSec: next.durationSec,
        });
        const winnerName = next.winner === 'A' ? next.sideA : next.sideB;
        const loserName = next.winner === 'A' ? next.sideB : next.sideA;
        updateElo(winnerName, loserName);
        const ump = incrementUmpire();
        setUmpireToast(ump.label);
        telemetry.matchCompleted({
          presetId: next.presetId,
          durationSec: next.durationSec,
          sideA: next.sideA,
          sideB: next.sideB,
          winner: next.winner,
        });
        haptic([100, 100, 100]);
      }
      return next;
    });
  };

  const handleUndo = (side) => {
    haptic(15);
    setState(prev => undoPoint(prev, side));
  };

  // Smart undo: pop the last scored side from history and undo it.
  const handleSmartUndo = () => {
    if (pointHistory.length === 0) return;
    haptic(15);
    const lastSide = pointHistory[pointHistory.length - 1];
    setPointHistory(h => h.slice(0, -1));
    setState(prev => undoPoint(prev, lastSide));
  };

  const game = state.games?.[state.currentGameIdx] || { scoreA: 0, scoreB: 0 };
  const gamesWonA = (state.games || []).filter(g => g.winner === 'A').length;
  const gamesWonB = (state.games || []).filter(g => g.winner === 'B').length;
  const isCompleted = !!state.winner;
  const preset = state.preset || PRESETS[state.presetId] || PRESETS.CLASSIC_21;

  const dispA = state.sideSwapped ? state.sideB : state.sideA;
  const dispB = state.sideSwapped ? state.sideA : state.sideB;
  const scoreLeft = state.sideSwapped ? game.scoreB : game.scoreA;
  const scoreRight = state.sideSwapped ? game.scoreA : game.scoreB;
  const sideKeyLeft = state.sideSwapped ? 'B' : 'A';
  const sideKeyRight = state.sideSwapped ? 'A' : 'B';

  const handleLiveShare = async () => {
    // If a live-watch session exists, share that URL. Else, auto-start one.
    let url;
    if (liveSession) {
      url = watchUrl(liveSession.shortId);
    } else {
      try {
        const res = await startLiveWatch({
          sideA: state.sideA,
          sideB: state.sideB,
          presetName: preset.name,
          bestOf: state.bestOf,
        });
        const session = { shortId: res.short_id, hostToken: res.host_token };
        setLiveSession(session);
        await pushLiveWatch(session.shortId, session.hostToken, {
          games: state.games,
          currentGameIdx: state.currentGameIdx,
          winner: state.winner,
        });
        url = watchUrl(session.shortId);
      } catch {
        toast.error('Could not start live session');
        return;
      }
    }
    const text = `🏸 🔴 LIVE: ${state.sideA} vs ${state.sideB} — watch the score:\n${url}`;
    if (navigator.share) {
      navigator.share({ url, text }).catch(() => { window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank'); });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const handleGoLive = async () => {
    if (liveSession) {
      // Already live → reopen the share card
      setShowLiveCard(true);
      return;
    }
    setGoingLive(true);
    try {
      const res = await startLiveWatch({
        sideA: state.sideA,
        sideB: state.sideB,
        presetName: preset.name,
        bestOf: state.bestOf,
      });
      const session = { shortId: res.short_id, hostToken: res.host_token };
      setLiveSession(session);
      // Immediately push the current state so viewers see actual scores, not zeros
      await pushLiveWatch(session.shortId, session.hostToken, {
        games: state.games,
        currentGameIdx: state.currentGameIdx,
        winner: state.winner,
      });
      setShowLiveCard(true);
      toast.success('You\'re live! Share the link with spectators.');
    } catch (err) {
      toast.error('Could not start live session');
      if (process.env.NODE_ENV !== 'production') console.debug('go-live failed:', err?.message);
    } finally {
      setGoingLive(false);
    }
  };

  const handleShareLiveUrl = () => {
    if (!liveSession) return;
    const url = watchUrl(liveSession.shortId);
    const text = `🏸 🔴 LIVE: ${state.sideA} vs ${state.sideB} — watch the score:\n${url}`;
    if (navigator.share) {
      navigator.share({ url, text }).catch(() => { window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank'); });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const handleCopyLiveUrl = async () => {
    if (!liveSession) return;
    try {
      await navigator.clipboard.writeText(watchUrl(liveSession.shortId));
      toast.success('Link copied');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.debug('copy failed:', err?.message);
      toast.error('Could not copy');
    }
  };

  // Per-game score arrays for mini-box display (left of main score in each column)
  const numBoxes = Math.max(state.bestOf || 1, 1);
  const leftSideKey = state.sideSwapped ? 'B' : 'A';
  const rightSideKey = state.sideSwapped ? 'A' : 'B';
  const leftGameScores = Array.from({ length: numBoxes }).map((_, i) => {
    const g = state.games?.[i];
    if (!g) return null;
    return leftSideKey === 'A' ? g.scoreA : g.scoreB;
  });
  const rightGameScores = Array.from({ length: numBoxes }).map((_, i) => {
    const g = state.games?.[i];
    if (!g) return null;
    return rightSideKey === 'A' ? g.scoreA : g.scoreB;
  });

  const handleRestart = () => {
    if (window.confirm('Restart this match? Current scores will be lost.')) {
      setPointHistory([]);
      setState(newMatchState({
        presetId: state.presetId,
        matchType: state.matchType,
        sideA: state.sideA,
        sideB: state.sideB,
        partnerA: state.partnerA,
        partnerB: state.partnerB,
        preset: state.preset,
      }));
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col safe-top safe-bottom" data-testid="quickplay-board">
      {/* ───────── HEADER: Back · SCOREBOARD title · Restart / Undo ───────── */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-border bg-card/80 gap-2">
        <Button variant="ghost" size="icon" onClick={onExit} className="h-9 w-9 shrink-0" data-testid="board-exit-btn">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </Button>
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-base sm:text-lg font-extrabold uppercase tracking-[0.18em]" data-testid="board-title">SCOREBOARD</h2>
          {liveSession && (
            <Badge className="bg-red-500 text-white border-0 text-[9px] font-extrabold uppercase px-1.5 py-0.5 flex items-center gap-1">
              <span className="inline-block h-1 w-1 rounded-full bg-white animate-pulse" /> LIVE
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleRestart}
            disabled={isCompleted}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border-2 border-orange-500 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 text-xs font-semibold hover:bg-orange-100 dark:hover:bg-orange-500/20 disabled:opacity-40 transition-colors"
            data-testid="board-restart-btn"
            title="Restart match"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            <span className="hidden xs:inline sm:inline">Restart</span>
          </button>
          <button
            onClick={handleSmartUndo}
            disabled={isCompleted || pointHistory.length === 0}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border-2 border-orange-500 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 text-xs font-semibold hover:bg-orange-100 dark:hover:bg-orange-500/20 disabled:opacity-40 transition-colors"
            data-testid="board-undo-btn"
            title="Undo last point"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6M3 13a9 9 0 1 0 3-7"/></svg>
            <span className="hidden xs:inline sm:inline">Undo</span>
          </button>
        </div>
      </div>

      {/* ───────── Secondary toolbar: Preset · Timer · Swap · Share · GoLive ───────── */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50 bg-card/40 gap-2 text-xs">
        <Badge variant="outline" className="text-[10px] font-mono">{preset.name}</Badge>
        <ElapsedTimer startedAt={state.startedAt} />
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={handleLiveShare} className="h-7 px-2" title="Share score" data-testid="board-live-share-btn">
            <Share2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant={liveSession ? 'default' : 'ghost'}
            size="sm"
            onClick={handleGoLive}
            disabled={goingLive}
            className={`h-7 px-2 ${liveSession ? 'bg-red-500/15 text-red-500 hover:bg-red-500/25 border border-red-500/30' : ''}`}
            title={liveSession ? 'Live — share link' : 'Go live'}
            data-testid="board-go-live-btn"
          >
            <Radio className={`h-3.5 w-3.5 ${liveSession ? 'animate-pulse' : ''}`} />
          </Button>
        </div>
      </div>

      {/* ───────── MAIN SCOREBOARD: Two columns split by divider ───────── */}
      <div className="flex-1 grid grid-cols-2 relative bg-white dark:bg-slate-950 min-h-[55vh]" data-testid="scoreboard-grid">
        {/* Vertical divider */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border -translate-x-1/2 pointer-events-none" />

        {/* Swap-side rotation icon at top center */}
        <button
          onClick={() => setState(p => ({ ...p, sideSwapped: !p.sideSwapped }))}
          className="absolute left-1/2 top-3 -translate-x-1/2 h-9 w-9 rounded-full border-2 border-border bg-card hover:bg-muted flex items-center justify-center z-10 transition-colors"
          title="Swap sides"
          data-testid="board-swap-btn"
        >
          <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
        </button>

        {/* LEFT PLAYER COLUMN */}
        <button
          onClick={() => !isCompleted && !showInterval && handleScore(sideKeyLeft)}
          disabled={isCompleted || showInterval}
          className="relative flex flex-col items-center justify-center px-2 py-6 group disabled:opacity-50 transition-colors hover:bg-orange-50/40 dark:hover:bg-orange-500/5 active:bg-orange-100/60 dark:active:bg-orange-500/10"
          data-testid="score-btn-left"
        >
          {/* Player name with serve dot */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            <p className="font-heading text-sm sm:text-base font-extrabold uppercase tracking-wider text-foreground truncate max-w-[140px] sm:max-w-[200px]">
              {dispA}
            </p>
            {state.serverSide === sideKeyLeft && (
              <span className="h-2 w-2 rounded-full bg-amber-400 inline-block" aria-label="serving" data-testid="serve-dot-left" />
            )}
          </div>
          {state.matchType === 'doubles' && (state.sideSwapped ? state.partnerB : state.partnerA) && (
            <p className="absolute top-10 left-1/2 -translate-x-1/2 text-[10px] text-muted-foreground truncate max-w-[140px]">
              & {state.sideSwapped ? state.partnerB : state.partnerA}
            </p>
          )}

          {/* Main score row: mini-boxes (left) + big score (right) */}
          <div className="flex items-center gap-3 sm:gap-5 mt-8">
            <div className="flex flex-col gap-1" data-testid="game-boxes-left">
              {leftGameScores.map((s, i) => (
                <div
                  key={`lbox-${i}`}
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-sm border flex items-center justify-center text-xs sm:text-sm font-mono font-bold tabular-nums ${
                    i === state.currentGameIdx
                      ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300'
                      : 'border-border bg-muted/30 text-foreground/70'
                  }`}
                  data-testid={`game-box-left-${i}`}
                >
                  {s !== null && s !== undefined ? s : 0}
                </div>
              ))}
            </div>
            <p className="font-heading text-7xl sm:text-8xl font-black text-foreground tabular-nums leading-none" data-testid="score-left">
              {scoreLeft}
            </p>
          </div>

          {/* +1 strip at bottom */}
          {!isCompleted && (
            <div className="absolute bottom-3 left-0 right-0 border-t border-orange-200 dark:border-orange-500/30 pt-3 mx-3">
              <p className="text-center font-heading text-xl sm:text-2xl font-extrabold text-orange-500">+1</p>
            </div>
          )}
        </button>

        {/* RIGHT PLAYER COLUMN */}
        <button
          onClick={() => !isCompleted && !showInterval && handleScore(sideKeyRight)}
          disabled={isCompleted || showInterval}
          className="relative flex flex-col items-center justify-center px-2 py-6 group disabled:opacity-50 transition-colors hover:bg-orange-50/40 dark:hover:bg-orange-500/5 active:bg-orange-100/60 dark:active:bg-orange-500/10"
          data-testid="score-btn-right"
        >
          {/* Player name with serve dot */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            <p className="font-heading text-sm sm:text-base font-extrabold uppercase tracking-wider text-foreground truncate max-w-[140px] sm:max-w-[200px]">
              {dispB}
            </p>
            {state.serverSide === sideKeyRight && (
              <span className="h-2 w-2 rounded-full bg-amber-400 inline-block" aria-label="serving" data-testid="serve-dot-right" />
            )}
          </div>
          {state.matchType === 'doubles' && (state.sideSwapped ? state.partnerA : state.partnerB) && (
            <p className="absolute top-10 left-1/2 -translate-x-1/2 text-[10px] text-muted-foreground truncate max-w-[140px]">
              & {state.sideSwapped ? state.partnerA : state.partnerB}
            </p>
          )}

          <div className="flex items-center gap-3 sm:gap-5 mt-8">
            <div className="flex flex-col gap-1" data-testid="game-boxes-right">
              {rightGameScores.map((s, i) => (
                <div
                  key={`rbox-${i}`}
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-sm border flex items-center justify-center text-xs sm:text-sm font-mono font-bold tabular-nums ${
                    i === state.currentGameIdx
                      ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300'
                      : 'border-border bg-muted/30 text-foreground/70'
                  }`}
                  data-testid={`game-box-right-${i}`}
                >
                  {s !== null && s !== undefined ? s : 0}
                </div>
              ))}
            </div>
            <p className="font-heading text-7xl sm:text-8xl font-black text-foreground tabular-nums leading-none" data-testid="score-right">
              {scoreRight}
            </p>
          </div>

          {!isCompleted && (
            <div className="absolute bottom-3 left-0 right-0 border-t border-orange-200 dark:border-orange-500/30 pt-3 mx-3">
              <p className="text-center font-heading text-xl sm:text-2xl font-extrabold text-orange-500">+1</p>
            </div>
          )}
        </button>
      </div>

      {/* Game-of-X indicator */}
      <p className="text-center text-muted-foreground text-[11px] py-2 bg-card/30">
        Game {state.currentGameIdx + 1} of {state.bestOf} · {gamesWonA}-{gamesWonB} games {state.sideSwapped && '· Swapped'}
      </p>

      {/* Looping Ad Banner */}
      <div className="px-3 pb-3">
        <LoopingAdBanner variant="inline" />
      </div>

      {/* Interval Overlay */}
      {showInterval && (
        <IntervalAdCard
          state={state}
          adConfig={adConfig}
          onResume={() => setShowInterval(false)}
        />
      )}

      {/* Brag Card */}
      {isCompleted && (
        <BragCard
          state={state}
          onClose={onExit}
          onNewMatch={onExit}
        />
      )}

      {/* Live Share Card */}
      {showLiveCard && liveSession && (
        <Dialog open onOpenChange={setShowLiveCard}>
          <DialogContent className="max-w-sm p-0 overflow-hidden gap-0" data-testid="live-share-card">
            <div className="bg-gradient-to-br from-red-500 via-rose-500 to-secondary p-5 text-center space-y-1">
              <Radio className="h-10 w-10 mx-auto text-white animate-pulse" />
              <p className="text-white/90 text-[10px] uppercase tracking-widest">You're Live</p>
              <p className="font-heading text-lg font-extrabold text-white uppercase">Share with Spectators</p>
            </div>
            <div className="p-5 space-y-3">
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Live URL</p>
                <p className="font-mono text-sm mt-1 break-all" data-testid="live-url-display">
                  {watchUrl(liveSession.shortId)}
                </p>
              </div>
              <Button onClick={handleShareLiveUrl} className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold" data-testid="live-share-whatsapp">
                <Share2 className="mr-2 h-4 w-4" /> Share to WhatsApp
              </Button>
              <Button onClick={handleCopyLiveUrl} variant="outline" className="w-full" data-testid="live-copy-url">
                <Copy className="mr-2 h-4 w-4" /> Copy Link
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                Score updates push automatically. Session expires in 4 hours.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Umpire badge toast */}
      {umpireToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-card border-2 border-primary rounded-full px-4 py-2 shadow-xl animate-in fade-in slide-in-from-top" data-testid="umpire-badge-toast" onAnimationEnd={() => setTimeout(() => setUmpireToast(null), 3000)}>
          <p className="text-xs font-bold flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" /> {umpireToast}
          </p>
        </div>
      )}
    </div>
  );
}

// ----------------------- Page Root -----------------------
export default function QuickPlay() {
  const [match, setMatch] = useState(null);

  if (match) {
    return <ScoringBoard initialState={match} onExit={() => setMatch(null)} />;
  }
  return (
    <>
      <SetupForm onStart={setMatch} />
      <div className="h-16" aria-hidden="true" />
      <BottomNav />
    </>
  );
}
